import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { RootState } from "../store";
import type { AppDispatch } from "../store";

/**
 * Standard API error structure
 */
export interface ApiError {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  traceId?: string;
  timestamp?: string;
  [key: string]: unknown;
}

/**
 * Extract error message from response or ApiError
 * Priority: detail > title > fallback message
 * 
 * @param error - Unknown error, ApiError object, or axios error
 * @returns Extracted error message
 */
export const extractErrorMessage = (error: unknown): string => {
  // If it's already an ApiError object
  if (error && typeof error === "object" && ("detail" in error || "title" in error)) {
    const apiError = error as ApiError;
    return apiError.detail || apiError.title || "An error occurred. Please try again.";
  }

  // If it's an axios error
  if (axios.isAxiosError(error) && error.response?.data) {
    const data = error.response.data as ApiError;
    return data.detail || data.title || "An error occurred. Please try again.";
  }

  // If it's a standard Error
  if (error instanceof Error) {
    return error.message;
  }

  return "An error occurred. Please try again.";
};

/**
 * Extract full error payload for rejectWithValue
 */
export const extractErrorPayload = (error: unknown): ApiError => {
  if (axios.isAxiosError(error) && error.response?.data) {
    return error.response.data as ApiError;
  }

  return {
    title: "Error",
    detail: error instanceof Error ? error.message : "An error occurred",
  };
};

/**
 * Simplified thunk API for payloadCreator
 */
type AppThunkAPI = {
  dispatch: AppDispatch;
  getState: () => RootState;
  requestId: string;
  signal: AbortSignal;
};

/**
 * Wrapper around createAsyncThunk with automatic error handling
 * 
 * All errors are automatically caught and transformed to standard ApiError format.
 * The error payload is passed to rejectWithValue for consistent error handling.
 * 
 * @example
 * export const login = createAppAsyncThunk(
 *   "auth/login",
 *   async (credentials, { dispatch }) => {
 *     dispatch(loginStart());
 *     const result = await authService.login(credentials);
 *     dispatch(loginSuccess());
 *     return result;
 *   }
 * );
 */
export const createAppAsyncThunk = <Returned, ThunkArg = void>(
  typePrefix: string,
  payloadCreator: (arg: ThunkArg, thunkAPI: AppThunkAPI) => Promise<Returned>
) => {
  return createAsyncThunk<
    Returned,
    ThunkArg,
    { state: RootState; dispatch: AppDispatch; rejectValue: ApiError }
  >(typePrefix, async (arg, thunkAPI) => {
    try {
      return await payloadCreator(arg, {
        dispatch: thunkAPI.dispatch,
        getState: thunkAPI.getState,
        requestId: thunkAPI.requestId,
        signal: thunkAPI.signal,
      });
    } catch (error: unknown) {
      // Extract error payload for rejectWithValue
      const errorPayload = extractErrorPayload(error);

      // Log for debugging
      console.error(`[${typePrefix}] Error:`, errorPayload);

      return thunkAPI.rejectWithValue(errorPayload);
    }
  });
};

